import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useCandidateAuth } from "@/contexts/CandidateAuthContext";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import BrandedHeader from "@/components/BrandedHeader";
import { Loader2, FileText, Video, Clock, CheckCircle, AlertCircle, User, LogOut, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function CandidateDashboardPage() {
  const { candidate, logout } = useCandidateAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await api.get("/candidate/dashboard");
      setDashboardData(response.data);
    } catch (error) {
      console.error("Failed to fetch dashboard:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success("Logged out successfully");
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { variant: "secondary", icon: Clock, text: "Pending", className: "bg-amber-500/20 text-amber-500" },
      started: { variant: "default", icon: TrendingUp, text: "In Progress", className: "bg-blue-500/20 text-blue-500" },
      in_progress: { variant: "default", icon: TrendingUp, text: "In Progress", className: "bg-blue-500/20 text-blue-500" },
      completed: { variant: "success", icon: CheckCircle, text: "Completed", className: "bg-success/20 text-success" }
    };
    
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    
    return (
      <Badge variant={badge.variant} className={badge.className}>
        <Icon className="w-3 h-3 mr-1" />
        {badge.text}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not started";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const handleStartTest = (testId, assignmentId) => {
    navigate(`/candidate/test/${testId}?assignment=${assignmentId}`);
  };

  const handleStartInterview = (jobId, sessionId) => {
    navigate(`/candidate/interview/${jobId}?session=${sessionId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const { upcoming = [], in_progress = [], completed = [] } = dashboardData || {};
  const hasNoAssessments = upcoming.length === 0 && in_progress.length === 0 && completed.length === 0;

  return (
    <div className="min-h-screen bg-background">
      <BrandedHeader>
        <div className="flex items-center justify-between w-full px-4">
          <h1 className="text-lg font-display font-semibold">My Dashboard</h1>
          <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn">
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </BrandedHeader>

      <div className="container mx-auto px-4 py-8 max-w-5xl">
        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="text-3xl font-display font-bold mb-2">
            Welcome back, {candidate?.name || "Candidate"}!
          </h2>
          <p className="text-muted-foreground">
            Track your assessments and application progress
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Tests</p>
                  <p className="text-2xl font-bold">{upcoming.filter(a => a.type === "test").length + in_progress.filter(a => a.type === "test").length}</p>
                </div>
                <FileText className="w-8 h-8 text-primary" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Interviews</p>
                  <p className="text-2xl font-bold">{upcoming.filter(a => a.type === "interview").length + in_progress.filter(a => a.type === "interview").length}</p>
                </div>
                <Video className="w-8 h-8 text-primary" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Completed</p>
                  <p className="text-2xl font-bold">{completed.length}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-success" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Empty State */}
        {hasNoAssessments && (
          <Card className="text-center py-12">
            <CardContent>
              <FileText className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-xl font-semibold mb-2">No Active Assessments</h3>
              <p className="text-muted-foreground">
                You're all caught up! We'll notify you when new opportunities are available.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Upcoming Assessments */}
        {upcoming.length > 0 && (
          <div className="mb-8">
            <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-primary" />
              Upcoming
            </h3>
            <div className="space-y-4">
              {upcoming.map((item, index) => (
                <AssessmentCard
                  key={index}
                  item={item}
                  onStartTest={handleStartTest}
                  onStartInterview={handleStartInterview}
                  getStatusBadge={getStatusBadge}
                  formatDate={formatDate}
                />
              ))}
            </div>
          </div>
        )}

        {/* In Progress */}
        {in_progress.length > 0 && (
          <div className="mb-8">
            <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-500" />
              In Progress
            </h3>
            <div className="space-y-4">
              {in_progress.map((item, index) => (
                <AssessmentCard
                  key={index}
                  item={item}
                  onStartTest={handleStartTest}
                  onStartInterview={handleStartInterview}
                  getStatusBadge={getStatusBadge}
                  formatDate={formatDate}
                />
              ))}
            </div>
          </div>
        )}

        {/* Completed */}
        {completed.length > 0 && (
          <div className="mb-8">
            <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-success" />
              Completed
            </h3>
            <div className="space-y-4">
              {completed.map((item, index) => (
                <AssessmentCard
                  key={index}
                  item={item}
                  onStartTest={handleStartTest}
                  onStartInterview={handleStartInterview}
                  getStatusBadge={getStatusBadge}
                  formatDate={formatDate}
                  isCompleted
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function AssessmentCard({ item, onStartTest, onStartInterview, getStatusBadge, formatDate, isCompleted = false }) {
  const isTest = item.type === "test";
  const data = isTest ? item.test : item.job;
  const assignment = isTest ? item.assignment : item.session;
  
  return (
    <Card className="hover:shadow-lg transition-shadow" data-testid={`assessment-card-${item.type}`}>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start gap-3 flex-1">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              {isTest ? (
                <FileText className="w-5 h-5 text-primary" />
              ) : (
                <Video className="w-5 h-5 text-primary" />
              )}
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-lg mb-1">{data?.title || "Untitled"}</h4>
              <p className="text-sm text-muted-foreground mb-2">
                {isTest ? "📝 MCQ Test" : "🎙️ AI Interview"}
                {data?.time_limit_minutes && ` • ${data.time_limit_minutes} min`}
                {data?.target_duration_minutes && ` • ${data.target_duration_minutes} min`}
                {data?.passing_score && ` • Passing: ${data.passing_score}%`}
              </p>
              {data?.description && (
                <p className="text-sm text-muted-foreground line-clamp-2">{data.description}</p>
              )}
            </div>
          </div>
          <div>
            {getStatusBadge(assignment?.status || "pending")}
          </div>
        </div>

        <div className="flex items-center justify-between pt-4 border-t">
          <div className="text-xs text-muted-foreground">
            {isCompleted ? (
              <>Completed on {formatDate(assignment?.completed_at)}</>
            ) : (
              <>Created {formatDate(assignment?.created_at)}</>
            )}
          </div>

          {!isCompleted && (
            <Button
              size="sm"
              onClick={() => isTest ? onStartTest(data?.id, assignment?.id) : onStartInterview(data?.id, assignment?.id)}
              data-testid={`start-${item.type}-btn`}
            >
              {assignment?.status === "started" || assignment?.status === "in_progress" ? "Continue" : "Start"} {isTest ? "Test" : "Interview"}
            </Button>
          )}

          {isCompleted && isTest && assignment?.score_pct !== null && (
            <div className="text-right">
              <p className="text-sm font-semibold">Score: {assignment.score_pct}%</p>
              {assignment.passed !== null && (
                <p className={`text-xs ${assignment.passed ? "text-success" : "text-destructive"}`}>
                  {assignment.passed ? "✓ Passed" : "✗ Not Passed"}
                </p>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
